import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import { useTranslation } from 'react-i18next';

const HEADING_FONT = "'Manrope', sans-serif";
const BODY_FONT = "'Inter', sans-serif";

const ContactFooter = ({ salesContact }) => {
  const { t } = useTranslation();

  if (!salesContact) return null;

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        bgcolor: '#006e2a',
        borderRadius: 2,
        p: 6,
        textAlign: 'center',
        color: 'white',
      }}
    >
      {/* Decorative circles */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: 256,
          height: 256,
          borderRadius: '50%',
          bgcolor: 'rgba(255,255,255,0.05)',
          mr: -4,
          mt: -4,
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: 192,
          height: 192,
          borderRadius: '50%',
          bgcolor: 'rgba(0,0,0,0.05)',
          ml: -3,
          mb: -3,
          pointerEvents: 'none',
        }}
      />

      <Typography
        sx={{
          fontFamily: HEADING_FONT,
          fontWeight: 800,
          fontSize: { xs: '1.75rem', md: '2.5rem' },
          mb: 2,
          position: 'relative',
          zIndex: 10,
        }}
      >
        {t('offer.contactTitle', 'Have questions about this offer?')}
      </Typography>

      <Typography
        sx={{
          fontFamily: BODY_FONT,
          color: 'rgba(255,255,255,0.8)',
          fontSize: { xs: '1rem', md: '1.125rem' },
          mb: 5,
          maxWidth: 560,
          mx: 'auto',
          position: 'relative',
          zIndex: 10,
        }}
      >
        {salesContact.name
          ? t('offer.contactSubtitleNamed', 'Contact {{name}} directly', { name: salesContact.name })
          : t('offer.contactSubtitle', 'Contact your account manager directly')}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          justifyContent: 'center',
          position: 'relative',
          zIndex: 10,
        }}
      >
        {salesContact.phone && (
          <Button
            component="a"
            href={`tel:${salesContact.phone}`}
            variant="contained"
            startIcon={<PhoneIcon />}
            sx={{
              bgcolor: 'white',
              color: '#006e2a',
              fontFamily: BODY_FONT,
              fontWeight: 900,
              borderRadius: 2,
              px: { xs: 3, sm: 5 },
              py: 2,
              fontSize: { xs: '0.9375rem', sm: '1rem' },
              lineHeight: 1.3,
              textTransform: 'none',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.92)' },
            }}
          >
            {t('offer.callManager', 'Call Manager')}
          </Button>
        )}

        {salesContact.email && (
          <Button
            component="a"
            href={`mailto:${salesContact.email}`}
            variant="outlined"
            startIcon={<EmailIcon />}
            sx={{
              borderColor: 'rgba(255,255,255,0.3)',
              borderWidth: 2,
              color: 'white',
              fontFamily: BODY_FONT,
              fontWeight: 900,
              borderRadius: 2,
              px: { xs: 3, sm: 5 },
              py: 2,
              fontSize: { xs: '0.9375rem', sm: '1rem' },
              lineHeight: 1.3,
              textTransform: 'none',
              bgcolor: '#006e2a',
              '&:hover': {
                borderColor: 'rgba(255,255,255,0.5)',
                borderWidth: 2,
                bgcolor: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            {t('offer.emailManager', 'Email')}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default ContactFooter;
